import {
  ActionMenu,
  ConfirmationDialog,
  useNotifications
} from '@ijoin/components';
import {
  GetAdminParticipantsRes,
  getGetAdminParticipantsQueryKey,
  useDeleteAdminPlanEnrollment,
  useGetPlan,
  usePostAdminForceOutReprocess,
  usePostAdminMapReanalysis,
  usePostAdminParticipantLiteSyncForParticipant,
  usePostAdminParticipantSync,
  usePostAdminRemoveFromMap,
  usePostAdminSsnReset
} from '@ijoin/hooks';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { FC, useState } from 'react';
import { useToggle } from 'usehooks-ts';

import { MapDefaultDialog } from './MapDefaultDialog';
import { MapOptOutDialog } from './MapOptOutDialog';

export const ParticipantActionsCell: FC<{
  row: GetAdminParticipantsRes;
}> = props => {
  const notifications = useNotifications();
  const queryClient = useQueryClient();
  const [isDeleteOpen, toggleDeleteOpen] = useToggle();
  const [isDemoResetOpen, toggleDemoResetOpen] = useToggle();
  const [isForceOutOpen, toggleForceOutOpen] = useToggle();
  const [isLiteSyncOpen, toggleLiteSyncOpen] = useToggle();
  const [isMapDefaultOpen, toggleMapDefaultOpen] = useToggle();
  const [isMapOptOutOpen, toggleMapOptOutOpen] = useToggle();
  const [isMapReanalysisOpen, toggleMapReanalysisOpen] = useToggle();
  const [isRemoveFromMapOpen, toggleRemoveFromMapOpen] = useToggle();
  const [isSyncOpen, toggleSyncOpen] = useToggle();
  const [isMenuOpened, setMenuOpened] = useState(false);

  const plan = useGetPlan(props.row.planId, {
    query: { enabled: isMenuOpened }
  });

  const onOpenMenu = () => setMenuOpened(true);

  const onImpersonate = () =>
    window.open(
      `/api/v3/plans/${props.row.planId}/participants/${props.row.participantId}/impersonate`,
      '_blank'
    );

  const onDownloadDebugSnapshot = () =>
    window.open(
      `/api/v3/admin/plans/${props.row.planId}/enrollments/${props.row.planEnrollmentId}/debug-snapshot`,
      '_blank'
    );

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: getGetAdminParticipantsQueryKey()
    });

  const forceOutReprocess = usePostAdminForceOutReprocess({
    mutation: {
      onError: () =>
        notifications.error('Failed to trigger force out reprocess'),
      onSuccess: () =>
        notifications.success('Force out reprocess triggered successfully')
    }
  });

  const mapReanalysis = usePostAdminMapReanalysis({
    mutation: {
      onError: () => notifications.error('Failed to trigger MAP reanalysis'),
      onSuccess: () =>
        notifications.success('MAP reanalysis triggered successfully')
    }
  });

  const participantLiteSync = usePostAdminParticipantLiteSyncForParticipant({
    mutation: {
      onError: () =>
        notifications.error('Failed to trigger participant lite sync'),
      onSuccess: () =>
        notifications.success('Participant lite sync triggered successfully')
    }
  });

  const participantSync = usePostAdminParticipantSync({
    mutation: {
      onError: () => notifications.error('Failed to sync the participant'),
      onSuccess: () => {
        invalidate();
        notifications.success('Participant synced successfully');
      }
    }
  });

  const removeFromMap = usePostAdminRemoveFromMap({
    mutation: {
      onError: () => notifications.error('Failed to remove from MAP'),
      onSuccess: () => {
        invalidate();
        notifications.success('Participant removed from MAP successfully');
      }
    }
  });

  const ssnReset = usePostAdminSsnReset({
    mutation: {
      onError: () => notifications.error('Failed to run demo reset'),
      onSuccess: () => {
        invalidate();
        notifications.success('Demo reset completed successfully');
      }
    }
  });

  const deleteEnrollment = useDeleteAdminPlanEnrollment({
    mutation: {
      onError: () => notifications.error('Failed to delete enrollment'),
      onSuccess: () => {
        invalidate();
        notifications.success('Enrollment deletion initiated');
      }
    }
  });

  const onConfirmDelete = () => {
    toggleDeleteOpen();
    deleteEnrollment.mutate({
      planEnrollmentId: props.row.planEnrollmentId,
      planId: props.row.planId
    });
  };

  const onConfirmDemoReset = () => {
    toggleDemoResetOpen();
    ssnReset.mutate({
      data: { planEnrollmentId: props.row.planEnrollmentId },
      planId: props.row.planId
    });
  };

  const onConfirmLiteSync = () => {
    toggleLiteSyncOpen();
    participantLiteSync.mutate({
      participantId: props.row.participantId,
      planId: props.row.planId
    });
  };

  const onConfirmSync = () => {
    toggleSyncOpen();
    participantSync.mutate({
      participantId: props.row.participantId,
      planId: props.row.planId
    });
  };

  const onConfirmForceOut = () => {
    toggleForceOutOpen();
    forceOutReprocess.mutate({
      planEnrollmentId: props.row.planEnrollmentId,
      planId: props.row.planId
    });
  };

  const onConfirmMapReanalysis = () => {
    toggleMapReanalysisOpen();
    mapReanalysis.mutate({
      participantId: props.row.participantId,
      planId: props.row.planId
    });
  };

  const onConfirmRemoveFromMap = () => {
    toggleRemoveFromMapOpen();
    removeFromMap.mutate({
      planEnrollmentId: props.row.planEnrollmentId,
      planId: props.row.planId
    });
  };

  return (
    <>
      <ActionMenu
        actions={[
          props.row.hasActiveMapProgram &&
            !props.row.inMap && {
              content: 'Default into MAP',
              onSelect: toggleMapDefaultOpen
            },
          { content: 'Delete Enrollment', onSelect: toggleDeleteOpen },
          props.row.socialSecurityNumber && {
            content: 'Demo Reset',
            onSelect: toggleDemoResetOpen
          },
          {
            content: 'Download Debug Snapshot',
            onSelect: onDownloadDebugSnapshot
          },
          (plan.isPending || plan.data?.isSyncable) && {
            content: 'Full Participant Sync',
            disabled: plan.isPending || participantSync.isPending,
            onSelect: toggleSyncOpen,
            tooltip: participantSync.isPending ? 'Sync in progress' : undefined
          },
          { content: 'Impersonate', onSelect: onImpersonate },
          props.row.entryExitTypeCode &&
            !['O', 'P'].includes(props.row.entryExitTypeCode) && {
              content: 'Opt Out of MAP',
              onSelect: toggleMapOptOutOpen
            },
          (plan.isPending || plan.data?.isRelius) && {
            content: 'Participant Lite Sync',
            disabled: plan.isPending || participantLiteSync.isPending,
            onSelect: toggleLiteSyncOpen,
            tooltip: participantLiteSync.isPending
              ? 'Sync in progress'
              : undefined
          },
          props.row.inMap && {
            content: 'Remove from MAP',
            onSelect: toggleRemoveFromMapOpen
          },
          (plan.isPending || plan.data?.enableAutoForceOut) && {
            content: 'Trigger Force Out',
            disabled: plan.isPending,
            onSelect: toggleForceOutOpen
          },
          props.row.inMap && {
            content: 'Trigger MAP Reanalysis',
            onSelect: toggleMapReanalysisOpen
          }
        ]}
        onOpen={onOpenMenu}
        trigger={
          <IconButton data-testid={`actions-${props.row.participantId}`}>
            <MoreVertIcon />
          </IconButton>
        }
      />
      <ConfirmationDialog
        confirmationText='DELETE'
        confirmColor='error'
        confirmText='Delete'
        data-testid='deleteEnrollment'
        isOpen={isDeleteOpen}
        message={`This will permanently delete the enrollment for ${props.row.firstName} ${props.row.lastName} and all associated data. This action is irreversible.`}
        onCancel={toggleDeleteOpen}
        onConfirm={onConfirmDelete}
        title='Delete Enrollment'
      />
      <ConfirmationDialog
        confirmColor='error'
        confirmText='Reset'
        data-testid='demoResetParticipantMenuItem'
        isOpen={isDemoResetOpen}
        message={`This will unfinalize enrollments, delete export queue entries, and delete allocations for SSN ${props.row.socialSecurityNumber} within this plan.`}
        onCancel={toggleDemoResetOpen}
        onConfirm={onConfirmDemoReset}
        title='Demo Reset'
      />
      <ConfirmationDialog
        confirmText='Sync'
        data-testid='participantLiteSync'
        isOpen={isLiteSyncOpen}
        message={`This will trigger a participant lite sync for ${props.row.firstName} ${props.row.lastName}.`}
        onCancel={toggleLiteSyncOpen}
        onConfirm={onConfirmLiteSync}
        title='Participant Lite Sync'
      />
      <ConfirmationDialog
        confirmText='Sync'
        data-testid='participantSync'
        isOpen={isSyncOpen}
        message={`This will pull ${props.row.firstName} ${props.row.lastName} fresh from the record keeper, bypassing the 24 hour freshness window. It can take a moment to complete.`}
        onCancel={toggleSyncOpen}
        onConfirm={onConfirmSync}
        title='Full Participant Sync'
      />
      <ConfirmationDialog
        confirmationText='FORCE OUT'
        confirmColor='error'
        confirmText='Trigger'
        data-testid='forceOutReprocess'
        isOpen={isForceOutOpen}
        message={`This will trigger automatic force out processing for ${props.row.firstName} ${props.row.lastName}. The participant will be added to the force out queue.`}
        onCancel={toggleForceOutOpen}
        onConfirm={onConfirmForceOut}
        title='Trigger Force Out'
      />
      <ConfirmationDialog
        confirmText='Trigger'
        data-testid='mapReanalysis'
        isOpen={isMapReanalysisOpen}
        message={`This will trigger MAP reanalysis for ${props.row.firstName} ${props.row.lastName}. Their managed account allocation will be recalculated.`}
        onCancel={toggleMapReanalysisOpen}
        onConfirm={onConfirmMapReanalysis}
        title='Trigger MAP Reanalysis'
      />
      <ConfirmationDialog
        confirmationText='REMOVE'
        confirmColor='error'
        confirmText='Remove'
        data-testid='removeFromMap'
        isOpen={isRemoveFromMapOpen}
        message={`This will remove ${props.row.firstName} ${props.row.lastName} from the Managed Account Program. Their MAP balances will be deleted and enrollment path will be cleared. This action is irreversible.`}
        onCancel={toggleRemoveFromMapOpen}
        onConfirm={onConfirmRemoveFromMap}
        title='Remove from MAP'
      />
      {isMapDefaultOpen && (
        <MapDefaultDialog
          onClose={toggleMapDefaultOpen}
          participantName={`${props.row.firstName} ${props.row.lastName}`}
          planEnrollmentId={props.row.planEnrollmentId}
        />
      )}
      {isMapOptOutOpen && (
        <MapOptOutDialog
          onClose={toggleMapOptOutOpen}
          participantName={`${props.row.firstName} ${props.row.lastName}`}
          planEnrollmentId={props.row.planEnrollmentId}
        />
      )}
    </>
  );
};
